import { Box } from '@chakra-ui/react';
import FakeLogin from './FakeLogin';
import CloseIcon from '@material-ui/icons/Close';
import ImageLoader from '../../../../components/v4/ImageLoader';
import { useMemo, useState, useEffect } from 'react';

import TCAPFavicon from '../../../../assets/images/TCAP-Favicon.svg';

export default function PartnerLoginPreview({
    logo,
    favicon,
    brandName = 'TCAP',
    email = '',
    backgroundOptions,
    // dirty prop, used to overide padding, cause we're reusing
    // this component on partner trials, and I need a quick way to change
    // padding
    paddingOveride = { padding: '25px 0' },
    dataWalkthroughId,
}) {
    const [asyncBackgroundImage, setAsyncBackgroundImage] = useState(null);

    // Handle async background images (Bing / NASA)
    useEffect(() => {
        if (backgroundOptions?.backgroundType === 3) {
            // Bing image of the day — proxied via TCAP backend to avoid CORS
            fetch(`${import.meta.env.VITE_APP_API_URL}/theme/bing-image`, {
                method: 'GET',
            })
                .then(async (response) => {
                    const { url } = await response.json();
                    setAsyncBackgroundImage(`url(${url})`);
                })
                .catch(() => setAsyncBackgroundImage(null));
        } else if (backgroundOptions?.backgroundType === 4) {
            // NASA image of the day
            fetch(
                'https://api.nasa.gov/planetary/apod?api_key=VKrdDxR2J9gaBTICmIWsDGN57FIiOLFg4tZaQKHe&thumbs=true',
                { method: 'GET' },
            )
                .then(async (response) => {
                    const { hdurl, thumbnail_url } = await response.json();
                    setAsyncBackgroundImage(`url(${hdurl || thumbnail_url})`);
                })
                .catch(() => {
                    setAsyncBackgroundImage(
                        'url(https://apod.nasa.gov/apod/image/2311/M1_Webb_4096.jpg)',
                    );
                });
        } else {
            setAsyncBackgroundImage(null);
        }
    }, [backgroundOptions?.backgroundType]);

    // Convert backgroundOptions to a background image URL based on type
    const backgroundImage = useMemo(() => {
        if (!backgroundOptions) return undefined;

        // If we have an async background image (NASA), use it
        if (asyncBackgroundImage) return asyncBackgroundImage;

        switch (backgroundOptions.backgroundType) {
            // Default bg color
            case 0:
                return 'linear-gradient(#F5F5F5, #F5F5F5)';

            // Solid defined bg color
            case 1: {
                const bgColor = backgroundOptions.backgroundColor?.includes('#')
                    ? backgroundOptions.backgroundColor
                    : `#${backgroundOptions.backgroundColor}`;
                return `linear-gradient(${bgColor}, ${bgColor})`;
            }

            // Defined bg image
            case 2:
                return backgroundOptions.backgroundImage
                    ? `url(${backgroundOptions.backgroundImage})`
                    : undefined;

            // Bing / NASA image of the day — both handled by useEffect above
            case 3:
            case 4:
                return undefined;

            default:
                return undefined;
        }
    }, [backgroundOptions, asyncBackgroundImage]);

    return (
        <Box sx={{ display: 'flex', flexDir: 'column', width: '100%' }}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    flexDir: 'column',
                    width: '100%',
                    //shadow: "xl",
                    padding: '25px 0',
                    ...paddingOveride,
                }}>
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                    }}>
                    {/* Tab */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '7px 7px 5px 7px',
                            border: '1px solid #DDD',
                            borderBottom: 'none',
                            background: '#FAFAFA',
                            borderTopLeftRadius: '10px',
                            borderTopRightRadius: '10px',
                            height: '40px',
                            width: '220px',
                            color: 'gray.700',
                        }}>
                        <ImageLoader
                            image={favicon || TCAPFavicon}
                            objectProps={{
                                alt: 'Domain Logo',
                                style: {
                                    width: '18px',
                                    margin: '0 5px 0 0',
                                },
                                type: 'image/svg+xml',
                            }}
                            imageProps={{
                                sx: {
                                    width: '18px',
                                    margin: '0 5px 0 0',
                                },
                            }}
                        />
                        {brandName}
                        <CloseIcon
                            style={{
                                marginLeft: 'auto',
                                color: '#777',
                                width: '0.75em',
                            }}
                        />
                    </Box>
                </Box>

                <Box
                    sx={{
                        width: '100%',
                        border: '1px solid #DDD',
                        borderBottomLeftRadius: '10px',
                        borderBottomRightRadius: '10px',
                        overflow: 'hidden',
                    }}>
                    <FakeLogin
                        dataWalkthroughId={dataWalkthroughId}
                        logo={logo}
                        email={email}
                        backgroundImage={backgroundImage}
                    />
                </Box>
            </Box>
        </Box>
    );
}
